import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowClockwise,
  CheckCircle,
  CircleNotch,
  FilmReel,
  ImageSquare,
  Warning,
} from "@phosphor-icons/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useCriativos } from "@/features/criativos/hooks/useCriativos";
import { useDeleteCriativo } from "@/features/criativos/hooks/useDeleteCriativo";
import { useRenameCriativo } from "@/features/criativos/hooks/useRenameCriativo";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { AnalysisResult } from "@/features/criativos/components/AnalysisResult";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "@/lib/toast";
import { friendlyError, toastError } from "@/lib/userMessages";

function StatusBadge({ analysis }: { analysis: unknown }) {
  if (!analysis) {
    return (
      <Badge variant="secondary" className="gap-1 shrink-0">
        <CircleNotch className="w-3 h-3 animate-spin" />
        Processando
      </Badge>
    );
  }
  return (
    <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 shrink-0">
      <CheckCircle className="w-3 h-3" />
      Concluída
    </Badge>
  );
}

export default function CriativoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const list = useCriativos();
  const deleteCriativo = useDeleteCriativo();
  const renameCriativo = useRenameCriativo();

  const row = list.data?.find((c) => c.id === id) ?? null;
  usePageTitle(row?.label ?? "Criativo");

  return (
    <AppLayout>
      <div className="space-y-5 animate-fade-in">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/criativos")}
            className="gap-1.5"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
            Voltar
          </Button>
        </div>

        {list.isPending && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {list.isError && (
          <div className="flex items-start gap-3 bg-destructive/15 border border-destructive/30 rounded-lg p-4">
            <Warning weight="duotone" className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">Erro ao carregar</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {friendlyError(list.error, "Não foi possível carregar o criativo. Tente atualizar a página.")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto gap-1.5"
              onClick={() => void list.refetch()}
            >
              <ArrowClockwise weight="bold" className="w-3.5 h-3.5" />
              Tentar novamente
            </Button>
          </div>
        )}

        {!list.isPending && !list.isError && !row && (
          <div className="text-center py-12">
            <p className="text-sm font-medium text-foreground">Criativo não encontrado</p>
            <p className="text-xs text-muted-foreground mt-1">
              Pode ter sido removido. <Link to="/criativos" className="text-primary hover:underline">Ver lista</Link>
            </p>
          </div>
        )}

        {row && (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
                  {row.media_kind === "video" ? (
                    <FilmReel weight="duotone" className="w-5 h-5 text-primary" />
                  ) : (
                    <ImageSquare weight="duotone" className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-foreground min-w-0">
                    <InlineEditText
                      value={row.label || ""}
                      onSave={async (newName) => {
                        try {
                          await renameCriativo.mutateAsync({ id: row.id, label: newName });
                          toast.success("Renomeado com sucesso.");
                        } catch (err) {
                          toastError(err, "Não foi possível renomear. Tente novamente.");
                          throw err;
                        }
                      }}
                      placeholder="(sem rótulo)"
                    />
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {row.veiculacao} ·{" "}
                    {new Date(row.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <DeleteEntityButton
                  entityLabel="criativo"
                  confirmDescription="A análise será removida do seu histórico permanentemente."
                  onConfirm={async () => {
                    try {
                      await deleteCriativo.mutateAsync(row.id);
                      toast.success("Criativo excluído.");
                      navigate("/criativos");
                    } catch (err) {
                      toastError(err, "Não foi possível excluir. Tente novamente.");
                      throw err;
                    }
                  }}
                  data-testid="criativo-detail-delete"
                />
                <StatusBadge analysis={row.analysis_payload} />
              </div>
            </div>

            {row.objetivo_input && (
              <div className="bg-muted/20 border border-border/40 rounded-lg p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Objetivo informado
                </p>
                <p className="text-sm text-foreground/90">{row.objetivo_input}</p>
              </div>
            )}

            {row.analysis_payload ? (
              <AnalysisResult
                analysis={row.analysis_payload}
                transcript={row.transcript_text ?? ""}
                veiculacao={row.veiculacao}
              />
            ) : (
              <div className="bg-card border border-border/50 rounded-xl p-6 text-center">
                <CircleNotch className="w-6 h-6 animate-spin mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Análise ainda processando…
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
